import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as HotToaster } from "react-hot-toast";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AuthProvider } from "./contexts/AuthContext";
import { ConsentProvider } from "./contexts/ConsentContext";
import ProtectedRoute from './components/ProtectedRoute';
import CookieBanner from './components/lgpd/CookieBanner';

// Eager loading: Páginas públicas principais (carregamento inicial)
import SiteLayout from "./layouts/SiteLayout";
import Index from "./pages/Index";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import NotFound from "./pages/NotFound";

// Lazy loading: Páginas de autenticação (não críticas para inicial)
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const ConfirmAccountDeletion = lazy(() => import('./pages/ConfirmAccountDeletion'));

// Lazy loading: Páginas públicas secundárias
const About = lazy(() => import("@pages/About"));
const Services = lazy(() => import("@pages/Services"));
const Portfolio = lazy(() => import("@pages/Portfolio"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("@pages/FAQ"));
const ComoOrcamos = lazy(() => import("@pages/PricingMethodology"));
const ProjectDetails = lazy(() => import("@pages/ProjectDetails"));
const Profile = lazy(() => import("@pages/Profile"));
const PrivacyPolicy = lazy(() => import("@pages/PrivacyPolicy"));

// Lazy loading: Área administrativa (pesada, carrega apenas quando necessário)
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const UsersPage = lazy(() => import("./pages/admin/UsersPage"));
const ProjectsPage = lazy(() => import("@pages/admin/ProjectsPage"));
const ProjectDetailsPage = lazy(() => import("@pages/admin/ProjectDetailsPage"));
const PaymentsPage = lazy(() => import("@pages/admin/PaymentsPage"));
const SubscriptionsPage = lazy(() => import("@pages/admin/SubscriptionsPage"));
const QuotesPage = lazy(() => import('@/pages/admin/QuotesPage'));
const SettingsPage = lazy(() => import('@/pages/admin/SettingsPage'));
const ReportsPage = lazy(() => import('@/pages/admin/ReportsPage'));
const DesignSystem = lazy(() => import('@/pages/admin/DesignSystem'));
const PortfolioManagement = lazy(() => import('@/pages/admin/PortfolioManagement'));
const SystemMetricsPage = lazy(() => import('@/pages/admin/SystemMetricsPage'));
const AutomationsPage = lazy(() => import('@/pages/admin/AutomationsPage'));
const SupportTicketsPage = lazy(() => import('@/pages/admin/SupportTicketsPage'));
const AdminAgent = lazy(() => import('@/pages/admin/AdminAgent'));

// Lazy loading: Área do cliente (pesada, carrega apenas quando necessário)
const ClientLayout = lazy(() => import("./pages/client/ClientLayout"));
const ClientDashboard = lazy(() => import("./pages/client/ClientDashboard"));
const ClientProfile = lazy(() => import("./pages/client/ClientProfile"));
const ClientPayments = lazy(() => import("./pages/client/ClientPayments"));
const ClientNotifications = lazy(() => import("./pages/client/ClientNotifications"));
const ClientDocuments = lazy(() => import("./pages/client/ClientDocuments"));
const ClientProjectDetails = lazy(() => import("@pages/client/ClientProjectDetails"));
const PrivacyCenter = lazy(() => import("./pages/client/PrivacyCenter"));


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,     // dados ficam frescos por 5 minutos
      gcTime: 1000 * 60 * 10,       // cache mantido por 10 minutos
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Loading component para exibir durante o carregamento lazy
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <HotToaster position="top-right" />
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <AuthProvider>
          <ConsentProvider>
            <CookieBanner />
            <Suspense fallback={<PageLoader />}>
              <Routes>
              <Route path="/verify-email/:token" element={<VerifyEmail />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/confirm-account-deletion/:token" element={<ConfirmAccountDeletion />} />

            <Route element={<SiteLayout />}>
              <Route path="/" element={<Index />} />
              <Route path="/servicos" element={<Services />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/sobre" element={<About />} />
              <Route path="/contato" element={<Contact />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/como-orcamos" element={<ComoOrcamos />} />
              <Route path="/portfolio/:id" element={<ProjectDetails />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />

              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />

              <Route path="/profile" element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } />

            </Route>

            <Route path="/client" element={
                <ProtectedRoute>
                  <ClientLayout />
                </ProtectedRoute>
              }>
                <Route index element={<ClientDashboard />} />
                <Route path="projects/:id" element={<ClientProjectDetails />} />
                <Route path="profile" element={<ClientProfile />} />
                <Route path="payments" element={<ClientPayments />} />
                <Route path="notifications" element={<ClientNotifications />} />
                <Route path="documents" element={<ClientDocuments />} />
                <Route path="privacy-center" element={<PrivacyCenter />} />
              </Route>

              <Route path="*" element={<NotFound />} />

            <Route path="/admin" element={
              <ProtectedRoute requireAdmin>
                <AdminLayout />
              </ProtectedRoute>
            }>
              <Route index element={<AdminDashboard />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="projects" element={<ProjectsPage />} />
              <Route path="projects/:id" element={<ProjectDetailsPage />} />
              <Route path="payments" element={<PaymentsPage />} />
              <Route path="subscriptions" element={<SubscriptionsPage />} />
              <Route path="quotes" element={<QuotesPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="design-system" element={<DesignSystem />} />
              <Route path="portfolio-management" element={<PortfolioManagement />} />
              <Route path="system-metrics" element={<SystemMetricsPage />} />
              <Route path="automations" element={<AutomationsPage />} />
              <Route path="support" element={<SupportTicketsPage />} />
              <Route path="agent" element={<AdminAgent />} />
            </Route>

              </Routes>
            </Suspense>
          </ConsentProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
